import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@wundo/ui';
import { Sparkles } from 'lucide-react';
import { m } from '@/paraglide/messages.js';

export interface MeetingShareNotesDialogProps {
  open: boolean;
  pending: boolean;
  /**
   * The note as it stands, so re-opening after a failed write does not ask for
   * the paste again — null when there is none yet. The caller remounts the
   * dialog per opening, so this is read once.
   */
  initial: string | null;
  /** What submitting does; the notes are kept and a verslag written from them. */
  submitLabel: string;
  onSubmit: (content: string) => void;
  onCancel: () => void;
}

/**
 * The way in when Wundo was not in the room: one big field to paste your own
 * notes into, and one button that does the rest. It opens with whatever was
 * pasted before, so a second try starts from the first. There is never a list
 * of pastes to manage, and once the verslag exists this is not the way back in
 * — the document itself is what is edited then.
 */
export function MeetingShareNotesDialog({
  open,
  pending,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: MeetingShareNotesDialogProps) {
  const [notes, setNotes] = useState(initial ?? '');

  const submit = () => {
    const content = notes.trim();
    if (!content) return;
    onSubmit(content);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_report_share_notes()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3 px-5 py-4'>
          <p className='text-sm text-ink-muted'>{m.meeting_report_share_notes_body()}</p>
          {/*
            The field auto-grows with its content, which on an empty dialog
            collapses to one line — but this dialog IS the big paste field, so
            it opens at height and grows from there. Capped so a long paste
            scrolls inside the field and the footer stays on screen.
          */}
          <Textarea
            className='min-h-52 max-h-96'
            aria-label={m.meeting_report_material_add_label()}
            placeholder={m.meeting_report_material_add()}
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
          />
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_report_share_notes_cancel()}
          </Button>
          <Button
            size='sm'
            disabled={pending || notes.trim().length === 0}
            onClick={submit}
          >
            <Sparkles className='size-4' aria-hidden='true' />
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
